import React, { useState } from 'react';
import { string, arrayOf, shape } from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { Form, PrimaryButton, FieldCheckboxGroup } from '../../components';
import { useUpdateProfileMutation } from '../favourites/UserApi';
import css from './NotificationPreferences.module.css';

/**
 * NotificationPreferencesForm
 *
 * Stores selected notification types in currentUser.attributes.profile.protectedData.notifications[]
 *
 * @param {Object[]} props.options — [{ key: 'weeklyDigest', label: 'Weekly digest email' }, ...]
 * @param {Object}   props.currentUser
 */
const NotificationPreferencesForm = ({ currentUser, options, className }) => {
  const intl = useIntl();
  const [updateProfile, { isLoading, isError }] = useUpdateProfileMutation();
  const [submittedValues, setSubmittedValues] = useState({});

  const notifications =
    currentUser?.attributes?.profile?.protectedData?.notifications || [];

  const initialValues = { notifications };

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={values => {
        setSubmittedValues(values);
        return updateProfile({
          protectedData: { notifications: values.notifications || [] },
        });
      }}
      render={({ handleSubmit, values, invalid, pristine }) => {
        const pristineSinceLastSubmit =
          Object.keys(submittedValues).length > 0 && isEqual(values, submittedValues);
        const submitDisabled = invalid || pristine || pristineSinceLastSubmit || isLoading;

        return (
          <Form
            className={classNames(css.root, className)}
            onSubmit={e => { setSubmittedValues(values); handleSubmit(e); }}
          >
            <FieldCheckboxGroup
              className={css.checkboxGroup}
              id="notifications"
              name="notifications"
              options={options.map(o => ({
                key: o.key,
                label: o.label,
              }))}
            />

            {isError && (
              <p className={css.error}>
                <FormattedMessage id="NotificationPreferencesForm.error" />
              </p>
            )}

            <PrimaryButton
              type="submit"
              className={css.submitButton}
              inProgress={isLoading}
              disabled={submitDisabled}
              ready={pristineSinceLastSubmit}
            >
              <FormattedMessage id="NotificationPreferencesForm.save" />
            </PrimaryButton>
          </Form>
        );
      }}
    />
  );
};

NotificationPreferencesForm.defaultProps = {
  className: null,
  options: [],
};

NotificationPreferencesForm.propTypes = {
  className: string,
  options: arrayOf(shape({ key: string, label: string })),
};

export default NotificationPreferencesForm;
